import type { Faq } from '../components/FaqList'

export type ServiceOverride = {
  intro?: string
  whenNeeded?: string[]
  whatWeCheck?: string[]
  faqs?: Faq[]
}

/** Keyed by `${categorySlug}/${serviceSlug}` */
export const serviceOverrides: Record<string, ServiceOverride> = {
  'diagnostics-and-inspections/check-engine-light-diagnostics': {
    intro:
      'A check engine light means the vehicle stored a fault, not that a specific part has failed. We retrieve the stored information, test the systems it points to, and confirm the cause before recommending a repair.',
    whenNeeded: [
      'The check engine light came on and stayed on',
      'The light is flashing, or comes on and goes back off',
      'The light appeared along with rough running, hesitation or a loss of power',
      'A parts store scan gave you a code and you want it verified',
      'You need the vehicle checked before a longer drive',
    ],
    whatWeCheck: [
      'Stored fault information from the vehicle',
      'Live data from the systems the fault points to',
      'Related components that can set the same fault',
      'A plain-language explanation of the confirmed cause and the recommended correction',
    ],
    faqs: [
      {
        question: 'Does a code tell you which part to replace?',
        answer:
          'No. A code identifies a circuit or condition, not a failed part. Testing is what narrows it down to the actual cause.',
      },
      {
        question: 'Can I keep driving with the light on?',
        answer:
          'It depends on what set it. A flashing light or a light with a running complaint is worth calling about promptly rather than waiting.',
      },
      {
        question: 'What will you tell me after testing?',
        answer:
          'We explain the condition we verified, the cause we confirmed, and the correction we recommend, so you can decide what happens next.',
      },
    ],
  },
  'diagnostics-and-inspections/no-start-no-crank-diagnostics': {
    intro:
      'A vehicle that will not start or will not crank can involve the battery, the starting circuit, the charging system or a related fault. We test the circuit rather than replacing parts one at a time.',
    whenNeeded: [
      'The vehicle does not respond when you turn the key or press start',
      'It cranks slowly, or only starts sometimes',
      'It has needed a jump start more than once',
      'You hear a click but the engine does not turn over',
      'The vehicle started fine yesterday and will not start today',
    ],
    whatWeCheck: [
      'Battery condition and state of charge',
      'The starting circuit and its connections',
      'Charging system output',
      'Related faults that can prevent starting',
    ],
  },
  'diagnostics-and-inspections/fluid-leak-diagnosis': {
    intro:
      'A spot under the vehicle can be oil, coolant, transmission fluid, brake fluid or condensation. We identify what is leaking and where it is coming from before any repair is recommended.',
    whenNeeded: [
      'You noticed a spot or puddle where the vehicle was parked',
      'A fluid level keeps dropping between visits',
      'There is a burning or sweet smell after driving',
      'A warning light related to a fluid level came on',
      'You want a leak confirmed before a longer drive',
    ],
    whatWeCheck: [
      'Which fluid is leaking, identified on the vehicle',
      'The source of the leak, traced to its origin',
      'Fluid levels and the condition of the surrounding components',
      'The recommended correction and what to watch for in the meantime',
    ],
  },
  'diagnostics-and-inspections/noise-and-vibration-diagnosis': {
    intro:
      'Noises and vibrations are easiest to diagnose when we can reproduce them. We work from your description of when it happens, then test the systems that can produce that symptom.',
    whenNeeded: [
      'A new noise appeared while driving, braking or turning',
      'A vibration that changes with speed',
      'A clunk over bumps or when shifting into gear',
      'A humming or growling that changes when cornering',
      'A noise that only happens cold, or only at certain speeds',
    ],
    whatWeCheck: [
      'The conditions in which the noise or vibration occurs',
      'The systems that can produce that specific symptom',
      'Related components inspected at the same time',
      'The confirmed cause and the recommended correction',
    ],
  },
  'diagnostics-and-inspections/drivability-diagnostics': {
    intro:
      'Drivability concerns cover how the vehicle runs — hesitation, stumble, rough idle or a loss of power. Testing identifies which system is involved instead of replacing parts in sequence.',
    whenNeeded: [
      'The engine hesitates or stumbles under acceleration',
      'A rough idle, or the vehicle stalls',
      'A noticeable drop in power or fuel economy',
      'A check engine light along with a running complaint',
      'The vehicle runs differently cold than it does warm',
    ],
    whatWeCheck: [
      'The running complaint, verified on the vehicle',
      'Live data and testing of the systems involved',
      'Fuel, ignition and related components that can cause the symptom',
      'A clear explanation of the confirmed cause and the options',
    ],
  },
  'diagnostics-and-inspections/vehicle-safety-inspections': {
    intro:
      'A safety inspection is a review of the items that affect how safely the vehicle operates. You get a clear picture of the vehicle’s current condition and what should be addressed.',
    whenNeeded: [
      'You want the vehicle looked over before continuing to drive it',
      'A new-to-you vehicle you want reviewed',
      'It has been a while since the vehicle was inspected',
      'A concern with tires, brakes, steering or lighting',
      'You are preparing a vehicle for another driver in the family',
    ],
    whatWeCheck: [
      'Tires, tire pressure and visible tire condition',
      'Visible brake, steering and suspension concerns',
      'Fluid levels and visible leaks',
      'Lighting, wipers and any warning lights present',
    ],
    faqs: [
      {
        question: 'What do I get from the inspection?',
        answer:
          'A clear rundown of what we found, separated into what should be addressed now and what can be monitored.',
      },
      {
        question: 'Does an inspection include repairs?',
        answer:
          'No. An inspection identifies conditions. Any repair work is quoted and approved by you separately.',
      },
      {
        question: 'How do I schedule one?',
        answer:
          'Call the shop during business hours with your year, make and model, and we will get you on the schedule.',
      },
    ],
  },
  'diagnostics-and-inspections/general-vehicle-inspections': {
    intro:
      'A general inspection is a broad look at the vehicle’s current condition. It is a good starting point when you want to know where the vehicle stands rather than chasing a single symptom.',
    whenNeeded: [
      'You want to know the overall condition of the vehicle',
      'You recently bought the vehicle and want a baseline',
      'You are deciding whether to keep or replace the vehicle',
      'Maintenance history is unknown or incomplete',
      'You want a plan for what to address first',
    ],
    whatWeCheck: [
      'Tires, brakes, steering and suspension for visible concerns',
      'Fluid levels and visible leaks',
      'Belts, hoses and battery condition where accessible',
      'Lighting, wipers and any warning lights present',
    ],
    faqs: [
      {
        question: 'What do I receive from a general inspection?',
        answer:
          'An explanation of what we found and a practical order of priority for anything that needs attention.',
      },
      {
        question: 'Is this the same as a diagnostic?',
        answer:
          'No. An inspection reviews overall condition. Diagnosing a specific symptom involves targeted testing of that system.',
      },
      {
        question: 'How long does it take?',
        answer:
          'That depends on the vehicle and what we find. Call the shop and we will let you know what to expect for your visit.',
      },
    ],
  },
  'diagnostics-and-inspections/pre-purchase-vehicle-inspections': {
    intro:
      'A pre-purchase inspection gives you an independent look at a vehicle before you buy it, so the decision is based on its actual condition rather than its appearance.',
    whenNeeded: [
      'You are considering a used vehicle from a dealer or private seller',
      'You want a second opinion before completing a purchase',
      'The vehicle’s history or maintenance records are incomplete',
      'You want to know what the vehicle will likely need soon',
      'You are buying a vehicle for a new or younger driver',
    ],
    whatWeCheck: [
      'Visible mechanical condition of the major systems',
      'Fluid levels, fluid condition and visible leaks',
      'Tires, brakes, steering and suspension for visible concerns',
      'Warning lights and any stored fault information present',
    ],
    faqs: [
      {
        question: 'Can you inspect a vehicle I do not own yet?',
        answer:
          'Yes, as long as the seller allows the vehicle to be brought to the shop. Call us to arrange the timing.',
      },
      {
        question: 'Does an inspection guarantee the vehicle is problem free?',
        answer:
          'No. An inspection reports what is visible or testable at the time of the visit. It cannot predict future failures.',
      },
      {
        question: 'What should I bring?',
        answer:
          'Any service records the seller provided and a list of anything you noticed on the test drive.',
      },
    ],
  },
  'preventive-maintenance/oil-and-filter-changes': {
    intro:
      'Regular oil and filter service helps protect the engine and gives us a chance to review what else the vehicle is due for based on its service interval.',
    whenNeeded: [
      'An oil life indicator or maintenance reminder is showing',
      'You are unsure when the last oil service was performed',
      'The vehicle is driven mostly short trips or in heavy heat',
      'You are preparing for a longer drive',
      'You want the rest of the vehicle looked over at the same time',
    ],
    whatWeCheck: [
      'Engine oil and filter service for your vehicle',
      'Fluid levels and any visible leaks noticed during service',
      'Other maintenance items that are due or coming due',
      'Anything found during service that you should know about',
    ],
    faqs: [
      {
        question: 'How often should my vehicle have an oil service?',
        answer:
          'Intervals vary by vehicle and how it is driven. Call with your year, make, model and mileage and we can review the factory schedule with you.',
      },
      {
        question: 'Is there a current oil change offer?',
        answer:
          'Yes — a 5 qt oil change for $49.97, before taxes and shop fees. Not all oils apply. Diesel and specialty oils do not apply. Contact us for details.',
      },
      {
        question: 'Will you tell me what else is due?',
        answer:
          'We will let you know what is due now and what can reasonably wait, so you can plan the work out.',
      },
    ],
  },
  'preventive-maintenance/factory-scheduled-maintenance': {
    intro:
      'Factory scheduled maintenance follows the service plan your manufacturer set for the vehicle. Requirements vary by make, model and mileage, so we review what applies to yours.',
    whenNeeded: [
      'You are approaching a mileage-based service interval',
      'A maintenance reminder is showing on the dash',
      'Service history is unknown or incomplete',
      'You want to keep the vehicle on its factory schedule',
      'You are planning ownership over the long term',
    ],
    whatWeCheck: [
      'The maintenance items your vehicle is due for at its current mileage',
      'Fluid levels, filters and related service items',
      'Anything found during service that needs attention',
      'What is due now versus what can reasonably wait',
    ],
    faqs: [
      {
        question: 'How do I find out what my vehicle is due for?',
        answer:
          'Call the shop with your year, make, model and mileage and we can review the factory schedule with you.',
      },
      {
        question: 'Do I have to do everything at once?',
        answer:
          'No. We separate what is due now from what can wait so maintenance can be spread out sensibly.',
      },
      {
        question: 'Does this replace a diagnostic?',
        answer:
          'No. Scheduled maintenance is planned service. A specific symptom still calls for targeted diagnosis.',
      },
    ],
  },
  'preventive-maintenance/fluid-inspections-and-services': {
    intro:
      'Vehicle fluids have their own condition and service requirements. We check levels and condition, and go over which services your vehicle is actually due for.',
    whenNeeded: [
      'A fluid level is low or has needed topping off',
      'You are unsure when fluids were last serviced',
      'A visible leak or spot under the vehicle',
      'You are approaching a mileage-based service interval',
      'You want the vehicle reviewed before a longer drive',
    ],
    whatWeCheck: [
      'Fluid levels and visible condition',
      'Signs of leakage around the related components',
      'Which fluid services your vehicle is due for',
      'What can safely wait until the next visit',
    ],
    faqs: [
      {
        question: 'How often do fluids need service?',
        answer:
          'Requirements vary by vehicle and fluid. We go by the factory schedule for your specific vehicle rather than one universal interval.',
      },
      {
        question: 'Does low fluid always mean a leak?',
        answer:
          'Not always, but a level that keeps dropping is worth investigating rather than repeatedly topping off.',
      },
      {
        question: 'Can this be done with other maintenance?',
        answer:
          'Yes. Fluid service is commonly combined with other maintenance while the vehicle is here.',
      },
    ],
  },
  'brake-services/brake-fluid-service': {
    intro:
      'Brake fluid service addresses the hydraulic fluid your braking system depends on. Service requirements vary by vehicle, so we review what applies to yours.',
    whenNeeded: [
      'Your vehicle is at a mileage or time interval for brake fluid service',
      'Brake fluid condition was flagged during an inspection',
      'You are having other brake work performed',
      'The brake pedal feel has changed',
      'Service history for the brake system is unknown',
    ],
    whatWeCheck: [
      'Brake fluid level and visible condition',
      'The braking system for visible leakage',
      'Whether your vehicle is due for this service',
      'Anything else in the brake system that needs attention',
    ],
  },
  'steering-and-suspension/power-steering-fluid-service': {
    intro:
      'Power steering fluid service addresses the fluid the steering system relies on. Requirements vary by vehicle, and some systems do not use fluid at all, so we confirm what applies to yours.',
    whenNeeded: [
      'Steering feels heavier than it used to',
      'A whine or noise when turning',
      'A fluid level that has needed topping off',
      'A visible leak in the steering area',
      'You are at a mileage interval for the service',
    ],
    whatWeCheck: [
      'Fluid level and visible condition, where the system uses fluid',
      'The steering components for visible leakage',
      'Whether your vehicle is due for this service',
      'Related steering concerns found during inspection',
    ],
  },
  'tires-and-wheels/tire-rotation': {
    intro:
      'Tire rotation moves the tires to different positions so they wear more evenly over their life. Recommended intervals vary by vehicle and tire.',
    whenNeeded: [
      'You are at a mileage interval for rotation',
      'You have noticed uneven wear across the tread',
      'You are having other maintenance performed',
      'You are preparing for a longer drive',
      'You are unsure when the tires were last rotated',
    ],
    whatWeCheck: [
      'Current tire wear and condition at each position',
      'Tire pressure',
      'Anything visible that could be affecting wear',
      'Whether other tire or wheel service is recommended',
    ],
  },
  'tires-and-wheels/tire-pressure-inspection': {
    intro:
      'A tire pressure inspection confirms each tire is at the pressure your vehicle calls for, and looks for anything that could be causing a tire to lose air.',
    whenNeeded: [
      'A tire pressure warning light is on',
      'One tire seems to lose air over time',
      'The temperature has changed sharply',
      'You are preparing for a longer drive',
      'The vehicle feels different at highway speed',
    ],
    whatWeCheck: [
      'Pressure at each tire against your vehicle’s specification',
      'Visible tire condition and tread',
      'Anything visible that could be causing air loss',
      'Whether further tire or TPMS service is recommended',
    ],
  },
  'heating-and-air-conditioning/ac-leak-detection': {
    intro:
      'When an A/C system loses refrigerant, it is escaping somewhere. Leak detection locates the source so the repair addresses the cause rather than repeating a recharge.',
    whenNeeded: [
      'The A/C cooled well earlier in the season and no longer does',
      'The system has needed refrigerant added before',
      'Oily residue around A/C lines or fittings',
      'The compressor cycles on and off frequently or will not engage',
      'Cooling performance is inconsistent',
    ],
    whatWeCheck: [
      'System pressures on the high and low sides',
      'Hoses, lines, fittings and seals for visible signs of leakage',
      'Component condition in the areas where leaks commonly appear',
      'The findings, explained before any repair is recommended',
    ],
  },
  'heating-and-air-conditioning/ac-refrigerant-recovery-evacuation-and-recharge': {
    intro:
      'Recovery, evacuation and recharge is the service side of A/C work: removing what is in the system, evacuating it, and recharging to the specification for your vehicle. Testing comes first so we are not recharging a system that is losing refrigerant.',
    whenNeeded: [
      'Testing showed the system is low on refrigerant',
      'A repair was performed that opened the A/C system',
      'Cooling performance has dropped off',
      'The system has been recharged before and lost its charge again',
      'You want the system evaluated before the hot months',
    ],
    whatWeCheck: [
      'System pressures and current refrigerant level',
      'Whether refrigerant loss points to a leak that should be addressed first',
      'The correct refrigerant type and charge for your vehicle',
      'Cooling performance after the service',
    ],
  },
  'transmission-and-drivetrain/transmission-fluid-service': {
    intro:
      'Transmission fluid service addresses the fluid the transmission depends on. Requirements differ significantly between vehicles, so we confirm what your transmission calls for before servicing it.',
    whenNeeded: [
      'You are approaching a mileage interval for the service',
      'Shift quality has changed',
      'Fluid condition was flagged during an inspection',
      'A leak or low fluid level was found',
      'Service history for the transmission is unknown',
    ],
    whatWeCheck: [
      'Fluid level and visible condition',
      'The transmission and related components for visible leakage',
      'What service your specific transmission calls for',
      'Any shifting concerns you have described',
    ],
    faqs: [
      {
        question: 'Does every transmission need the same service?',
        answer:
          'No. Requirements vary by vehicle and transmission type, which is why we confirm what yours calls for first.',
      },
      {
        question: 'Will fluid service fix a shifting problem?',
        answer:
          'It may or may not. A shifting concern should be diagnosed so the recommendation reflects what is actually causing it.',
      },
      {
        question: 'What should I have ready when I call?',
        answer:
          'Year, make, model, mileage, and a description of anything you have noticed while driving.',
      },
    ],
  },
  'engine-repair/engine-tune-ups': {
    intro:
      'What a tune-up includes depends on the vehicle. We review what your engine is due for and what its current running condition suggests, then explain what is worth doing.',
    whenNeeded: [
      'The engine idles rough or hesitates',
      'A drop in fuel economy or power',
      'You are at a mileage interval for ignition-related maintenance',
      'Service history is unknown or incomplete',
      'A check engine light along with a running complaint',
    ],
    whatWeCheck: [
      'Current engine running condition',
      'Ignition-related maintenance items due for your vehicle',
      'Whether a running complaint needs diagnosis rather than maintenance',
      'What we recommend and why, before any work begins',
    ],
  },
  'cooling-system/coolant-antifreeze-service': {
    intro:
      'Coolant service addresses the fluid that keeps engine temperature in range. The correct coolant and interval vary by vehicle, so we confirm what yours requires.',
    whenNeeded: [
      'You are approaching a mileage or time interval for coolant service',
      'The coolant reservoir has needed topping off',
      'Coolant condition was flagged during an inspection',
      'Temperature has been running higher than normal',
      'Service history is unknown',
    ],
    whatWeCheck: [
      'Coolant level and visible condition',
      'The cooling system for visible leakage',
      'What coolant and interval your vehicle calls for',
      'Related cooling components inspected at the same time',
    ],
  },
  'electrical-system/fuse-and-relay-diagnosis': {
    intro:
      'A blown fuse or a failing relay is usually a symptom rather than the whole story. We trace the circuit to find why it is failing instead of simply replacing the fuse.',
    whenNeeded: [
      'A fuse keeps blowing after being replaced',
      'An accessory or circuit stopped working',
      'Something works intermittently',
      'Multiple items on the same circuit stopped at once',
      'A recent electrical accessory was added',
    ],
    whatWeCheck: [
      'The affected circuit and its fuses and relays',
      'Where the circuit is failing, traced on the vehicle',
      'Related wiring and connections',
      'The confirmed cause and the recommended repair',
    ],
  },
  'exhaust-and-emissions/evap-system-diagnostics-and-repair': {
    intro:
      'EVAP faults are usually the result of a leak somewhere in the fuel vapor system, and they are often small. Testing locates where before any part is replaced.',
    whenNeeded: [
      'A check engine light related to the EVAP system',
      'A fuel odor around the vehicle',
      'The light appeared after refueling',
      'A failed emissions-related readiness check',
      'The gas cap has been replaced and the light returned',
    ],
    whatWeCheck: [
      'Stored fault information related to the EVAP system',
      'Testing to locate where the system is leaking',
      'Related components, hoses and seals',
      'The confirmed cause and the recommended repair',
    ],
  },
  'additional-services/vehicle-maintenance-consultations': {
    intro:
      'A maintenance consultation is a conversation about your vehicle, not a repair. We go over its mileage, history and how you drive it, then lay out what to plan for and in what order.',
    whenNeeded: [
      'You are unsure what your vehicle needs next',
      'Service history is incomplete or unknown',
      'You want to budget upcoming maintenance instead of reacting to it',
      'You recently purchased the vehicle',
      'You are deciding how long to keep the vehicle',
    ],
    whatWeCheck: [
      'Your vehicle’s mileage, history and how it is driven',
      'The maintenance items its factory schedule calls for',
      'Anything you have noticed that may need to be looked at',
      'A practical order of priority for the work',
    ],
    faqs: [
      {
        question: 'Do I need to leave the vehicle?',
        answer:
          'Not necessarily. Call the shop and we can start with a conversation about the vehicle and what you want to plan for.',
      },
      {
        question: 'Will you tell me everything at once?',
        answer:
          'We separate what should be addressed now from what can reasonably wait, so the work can be spread out.',
      },
      {
        question: 'What should I have ready?',
        answer:
          'Year, make, model, current mileage, and any service records or concerns you want covered.',
      },
    ],
  },
  'additional-services/fleet-vehicle-maintenance-and-repair': {
    intro:
      'Fleet work is about keeping vehicles available. We handle maintenance and repair for small business fleets and communicate findings clearly so you can decide what to schedule and when.',
    whenNeeded: [
      'You run several vehicles for a small business',
      'You want maintenance handled on a consistent schedule',
      'A vehicle is down and you need it looked at',
      'You want one shop familiar with your vehicles',
      'You need findings explained clearly before approving work',
    ],
    whatWeCheck: [
      'Maintenance due across the vehicles you bring in',
      'Repair concerns reported by your drivers',
      'What needs attention now versus what can be scheduled',
      'Clear communication of findings before work is approved',
    ],
    faqs: [
      {
        question: 'How do we get started with fleet work?',
        answer:
          'Call the shop and tell us what vehicles you run and what you need covered. We will go from there.',
      },
      {
        question: 'Can you keep vehicles on a maintenance schedule?',
        answer:
          'We can review each vehicle’s factory schedule and mileage and tell you what is due and when.',
      },
      {
        question: 'Will you explain findings before repairs?',
        answer:
          'Yes. Findings and recommendations are explained before any work is approved, same as with any customer.',
      },
    ],
  },
  'additional-services/used-vehicle-inspections': {
    intro:
      'A used vehicle inspection gives you an independent look at a vehicle’s current condition, so you know what you are working with before or shortly after buying it.',
    whenNeeded: [
      'You are considering buying a used vehicle',
      'You recently bought one and want a baseline',
      'Service history is unknown or incomplete',
      'You want to know what the vehicle will likely need soon',
      'You are buying a vehicle for another driver in the family',
    ],
    whatWeCheck: [
      'Visible mechanical condition of the major systems',
      'Fluid levels, fluid condition and visible leaks',
      'Tires, brakes, steering and suspension for visible concerns',
      'Warning lights and any stored fault information present',
    ],
    faqs: [
      {
        question: 'Does the inspection guarantee the vehicle is sound?',
        answer:
          'No. It reports what is visible or testable at the time of the visit. It cannot predict future failures.',
      },
      {
        question: 'Can you inspect a vehicle I have not bought yet?',
        answer:
          'Yes, as long as the seller allows the vehicle to be brought to the shop.',
      },
      {
        question: 'What should I bring?',
        answer:
          'Any service records available and a note of anything you noticed while driving it.',
      },
    ],
  },
  'additional-services/road-trip-pre-trip-inspections': {
    intro:
      'A pre-trip inspection reviews the vehicle before extended travel and identifies visible maintenance or repair concerns you can address before you leave.',
    whenNeeded: [
      'You have a long trip coming up',
      'Warning lights or a change in drivability before travel',
      'Maintenance is overdue or the last service is unknown',
      'A concern with tires, brakes or fluid levels',
      'You are buying or preparing a vehicle for regular travel',
    ],
    whatWeCheck: [
      'Tires and tire pressure',
      'Visible brake, steering and suspension concerns',
      'Fluid levels and visible leaks',
      'Belts, hoses and battery condition where accessible',
      'Lights, wipers, warning lights and the concerns you reported',
    ],
    faqs: [
      {
        question: 'What should I bring or tell you?',
        answer:
          'Your year, make, model and mileage, your travel dates, and anything the vehicle has been doing that you want looked at.',
      },
      {
        question: 'How far ahead should I call?',
        answer:
          'Call as early as you can before the trip. That leaves room to address anything the inspection turns up before you leave.',
      },
      {
        question: 'Does an inspection guarantee a trouble-free trip?',
        answer:
          'No. An inspection identifies conditions that are visible or testable at the time of the visit. It cannot guarantee against future failures.',
      },
    ],
  },
}
